import React, { useEffect } from 'react';
import {
  AppBar,
  Box,
  Button,
  Container,
  CssBaseline,
  Paper,
  Stack,
  ThemeProvider,
  Toolbar,
  Typography,
} from '@mui/material';
import BluuTheme from 'assets/BluuTheme';
import { useIsAuthenticated, useMsal } from '@azure/msal-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { itsRequest } from 'http/authConfig';
import { useTranslation } from 'react-i18next';
import useWindowsDimension from 'functions/useWindowsDimension';

type PublicShellProps = {
  children: React.ReactNode;
};

const PublicShell: React.FC<PublicShellProps> = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { instance } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const { t } = useTranslation();
  const { height } = useWindowsDimension();

  const navItems = [
    { label: t('landing.nav.home'), path: '/landing' },
    { label: t('landing.nav.about'), path: '/about' },
    { label: t('landing.nav.pricing'), path: '/pricing' },
  ];

  // If user is already authenticated, send them straight into Bluu
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/', { replace: true });
    }
  }, [isAuthenticated, navigate]);

  const handleNav = (path: string) => {
    navigate(path);
  };

  const handleLogin = () => {
    if (isAuthenticated) {
      navigate('/');
      return;
    }
    instance.loginRedirect(itsRequest);
  };

  const handleRegister = () => {
    if (isAuthenticated) {
      navigate('/');
      return;
    }
    instance.loginRedirect(itsRequest);
  };

  return (
    <ThemeProvider theme={BluuTheme}>
      <CssBaseline />
      <Box
        sx={{
          width: '100%',
          minHeight: '100vh',
          background: `radial-gradient(circle at 20% 20%, rgba(255,255,255,0.04), transparent 35%), radial-gradient(circle at 80% 0%, rgba(255,255,255,0.04), transparent 35%), linear-gradient(180deg, #263238 0%, #1f272d 80%)`,
          color: 'primary.text',
        }}
      >
        <AppBar
          position='sticky'
          elevation={0}
          sx={{
            backgroundColor: 'rgba(38, 50, 56, 0.85)',
            backdropFilter: 'blur(12px)',
            borderBottom: '1px solid rgba(255,255,255,0.08)',
          }}
        >
          <Toolbar sx={{ display: 'flex', gap: 2 }}>
            <Typography
              variant='h6'
              sx={{ fontWeight: 800, letterSpacing: 0.5, cursor: 'pointer' }}
              onClick={() => handleNav('/landing')}
            >
              Bluu
            </Typography>
            <Stack direction='row' spacing={1} flex={1}>
              {navItems.map((item) => {
                const normalizedPath =
                  location.pathname === '/' ? '/landing' : location.pathname;
                const active = normalizedPath === item.path;
                return (
                  <Button
                    key={item.path}
                    color={active ? 'secondary' : 'inherit'}
                    onClick={() => handleNav(item.path)}
                    sx={{
                      textTransform: 'none',
                      fontWeight: active ? 700 : 500,
                    }}
                  >
                    {item.label}
                  </Button>
                );
              })}
            </Stack>
            <Stack direction='row' spacing={1}>
              <Button
                color='secondary'
                onClick={handleLogin}
                sx={{ textTransform: 'none' }}
              >
                {t('landing.nav.login')}
              </Button>
              <Button
                variant='contained'
                color='secondary'
                onClick={handleRegister}
                sx={{ textTransform: 'none' }}
              >
                {t('landing.nav.register')}
              </Button>
            </Stack>
          </Toolbar>
        </AppBar>

        <Container
          maxWidth={false}
          disableGutters
          sx={{
            width: '100%',
            flexGrow: 1,
            minHeight: 'auto',
            maxHeight: 'none',
            p: 2,
          }}
        >
          {children}
        </Container>
      <Box
        sx={{
          width: '100%',
          minHeight: '66px',
          maxHeight: '66px',
          background: `radial-gradient(circle at 20% 20%, rgba(255,255,255,0.04), transparent 35%), radial-gradient(circle at 80% 0%, rgba(255,255,255,0.04), transparent 35%), linear-gradient(180deg, #263238 0%, #1f272d 80%)`,
          color: 'primary.text',
        }}
      >
            <Paper
              variant='elevation3'
              sx={{
                p: 2,
                borderRadius: 3,
                background:
                  'linear-gradient(180deg, rgba(69,90,100,0.6) 0%, rgba(38,50,56,0.95) 100%)',
                border: '1px solid rgba(255,255,255,0.08)',
                width: '100%',
                minHeight: 80,
                maxHeight: 160,
                display: 'flex',
                justifyContent: 'flex-end',
                alignItems: 'flex-end',
              }}
            >
              @2024 Bluu AS.
            </Paper>
        </Box>
      </Box>

    </ThemeProvider>
  );
};

export default PublicShell;
